/**
 * Environment-aware Logger
 * Outputs logs only in development mode
 * @module utils/logger
 */
const LOG_LEVELS = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
};
/**
 * Detects if we're in development mode
 * Checks multiple indicators for Bloomreach environment
 */
function isDevEnvironment() {
    // This is a webpack check. If frontend was built in development mode ("npm run dev"), this will be true
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        return true;
    }
    if (typeof window !== 'undefined') {
        const hostname = window.location?.hostname || '';
        if (hostname.includes('grenke.bloomreach.cloud') || hostname === 'localhost') {
            return true;
        }
    }
    return false;
}
/**
 * Creates a logger instance
 */
function createLogger(config = {}) {
    const isDev = config.enabled ?? isDevEnvironment();
    const prefix = config.prefix ?? '🔧';
    const minLevel = config.minLevel ?? 'debug';
    function shouldLog(level) {
        if (!isDev)
            return false;
        return LOG_LEVELS[level] >= LOG_LEVELS[minLevel];
    }
    function formatMessage(level, message) {
        const timestamp = new Date().toLocaleTimeString();
        return `${prefix} [${timestamp}] [${level.toUpperCase()}] ${message}`;
    }
    return {
        /**
         * Debug level - detailed information for debugging
         */
        debug(message, ...args) {
            if (shouldLog('debug')) {
                console.debug(formatMessage('debug', message), ...args);
            }
        },
        /**
         * Info level - general information
         */
        info(message, ...args) {
            if (shouldLog('info')) {
                console.info(formatMessage('info', message), ...args);
            }
        },
        /**
         * Success message with checkmark
         */
        success(message, ...args) {
            if (shouldLog('info')) {
                console.info(`✅ ${message}`, ...args);
            }
        },
        /**
         * Warn level - potential issues
         */
        warn(message, ...args) {
            if (shouldLog('warn')) {
                console.warn(formatMessage('warn', message), ...args);
            }
        },
        /**
         * Error level - errors (always logged, even in production)
         */
        error(message, ...args) {
            // Errors are always logged
            console.error(formatMessage('error', message), ...args);
        },
        /**
         * Group related logs together
         */
        group(label) {
            if (isDev) {
                console.group(`${prefix} ${label}`);
            }
        },
        /**
         * End a log group
         */
        groupEnd() {
            if (isDev) {
                console.groupEnd();
            }
        },
        /**
         * Log a table (useful for arrays/objects)
         */
        table(data) {
            if (isDev) {
                console.table(data);
            }
        },
        /**
         * Log a DOM Object
         */
        object(data) {
            if (isDev) {
                console.dir(data);
            }
        },
        /**
         * Time a operation
         */
        time(label) {
            if (isDev) {
                console.time(`${prefix} ${label}`);
            }
        },
        /**
         * End timing and log result
         */
        timeEnd(label) {
            if (isDev) {
                console.timeEnd(`${prefix} ${label}`);
            }
        },
        /**
         * Check if logging is enabled
         */
        isEnabled() {
            return isDev;
        },
        /**
         * Create a child logger with a different prefix
         */
        child(childPrefix) {
            return createLogger({
                enabled: isDev,
                prefix: `${prefix} [${childPrefix}]`,
                minLevel,
            });
        },
    };
}
// Default logger instance for the component loader
export const logger = createLogger({
    prefix: 'ComponentLoader',
});
// Export factory for custom loggers
export { createLogger, isDevEnvironment };
